<template>
  <div class="crud_form_wrapper single_show_content_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.show_room_rent_order") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <div v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else>
        <!-- ==== Start:: Order Data Section ==== -->
        <div class="section-card mb-4">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.order_data") }}</h5>
          <div class="row">
            <!-- Order Number -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.order_number')"
              :value="'#' + data.id"
              disabled
            />
            
            <!-- Order Date -->
            <base-input
              col="4"
              type="text"
              :placeholder="$t('PLACEHOLDERS.order_date')"
              :value="formatDate(data.created_at)"
              disabled
            />
            
            <!-- Order Status -->
            <div class="col-4 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.order_status") }}</label>
              <v-chip
                :color="getStatusColor(data.status)"
                text-color="white"
                large
              >
                {{ data.status_label }}
              </v-chip>
            </div>
          </div>
        </div>
        <!-- ==== End:: Order Data Section ==== -->

        <!-- ==== Start:: Client Data Section ==== -->
        <div class="section-card mb-4" v-if="data.user">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.client_data") }}</h5>
          <div class="row">
            <!-- Client Avatar -->
            <div v-if="data.user.avatar" class="col-12 mb-3">
              <div class="preview-container text-center">
                <img :src="data.user.avatar" :alt="data.user.name" class="user-avatar" />
              </div>
            </div>

            <!-- Client Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.client_name')"
              :value="data.user.name"
              disabled
            />

            <!-- client mobile -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.phone')"
              :value="data.user.mobile"
              disabled
            />

            <!-- Client Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.user.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.user.rating }}/5</span>
              </div>
            </div>

            <!-- Client City -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.city')"
              :value="data.user.city || $t('TABLES.noData')"
              disabled
            />

            <!-- Client Location on Map -->
            <div class="col-12" v-if="data.user.latitude && data.user.longitude">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.client_location") }}</h6>
              <div class="map_wrapper">
                <GmapMap
                  :center="{
                    lat: parseFloat(data.user.latitude),
                    lng: parseFloat(data.user.longitude),
                  }"
                  :zoom="13"
                  map-type-id="terrain"
                  style="width: 100%; height: 300px"
                >
                  <GmapMarker
                    :position="{
                      lat: parseFloat(data.user.latitude),
                      lng: parseFloat(data.user.longitude)
                    }"
                    :clickable="false"
                    :draggable="false"
                  />
                </GmapMap>
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Client Data Section ==== -->

        <!-- ==== Start:: Stable Data Section ==== -->
        <div class="section-card mb-4" v-if="data.provider">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.stable_data") }}</h5>
          <div class="row">
            <!-- Stable Avatar -->
            <div v-if="data.provider.avatar" class="col-12 mb-3">
              <div class="preview-container text-center">
                <img :src="data.provider.avatar" :alt="data.provider.name" class="stable-avatar" />
              </div>
            </div>

            <!-- Stable Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.stable_name')"
              :value="data.provider.name"
              disabled
            />
            <!-- mobile Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.phone')"
              :value="data.provider.mobile"
              disabled
            />

            <!-- Stable Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.stable_rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.provider.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.provider.rating }}/5</span>
              </div>
            </div>

            <!-- Stable City -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.city')"
              :value="data.provider.city || $t('TABLES.noData')"
              disabled
            />

            <!-- Room Number (if order is accepted) -->
            <base-input
              v-if="data.status === 'accepted' && data.horse && data.horse.room"
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.room_number')"
              :value="data.horse.room"  
              disabled
            />

            <!-- Stable Location on Map -->
            <div class="col-12" v-if="data.provider.latitude && data.provider.longitude">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.stable_location") }}</h6>
              <div class="map_wrapper">
                <GmapMap
                  :center="{
                    lat: parseFloat(data.provider.latitude),
                    lng: parseFloat(data.provider.longitude),
                  }"
                  :zoom="13"
                  map-type-id="terrain"
                  style="width: 100%; height: 300px"
                >
                  <GmapMarker
                    :position="{
                      lat: parseFloat(data.provider.latitude),
                      lng: parseFloat(data.provider.longitude)
                    }"
                    :clickable="false"
                    :draggable="false"
                  />
                </GmapMap>
              </div>
            </div>
          </div>
        </div>
        <!-- ==== End:: Stable Data Section ==== -->

        <!-- ==== Start:: Horse Data Section ==== -->
        <div class="section-card mb-4" v-if="data.horse">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.horse_data") }}</h5>
          <div class="row">
            <!-- Horse Images -->
            <div class="col-12" v-if="data.horse.images && data.horse.images.length > 0">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.horse_images") }}</h6>
              <div class="images-container">
                <div
                  v-for="(image, index) in data.horse.images"
                  :key="index"
                  class="image-wrapper"
                  @click="showImageModal(image)"
                >
                  <img :src="image" :alt="`Horse Image ${index + 1}`" class="horse-image" />
                </div>
              </div>
            </div>

            <!-- Horse Video -->
            <div class="col-12" v-if="data.horse.video">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.horse_video") }}</h6>
              <video controls style="width: 100%; max-height: 400px;">
                <source :src="data.horse.video" type="video/mp4">
                {{ $t("MESSAGES.video_not_supported") }}
              </video>
            </div>

            <!-- Horse Name -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.horse_name')"
              :value="data.horse.name"
              disabled
            />

            <!-- Horse Rating -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.horse_rating") }}</label>
              <div class="rating-display">
                <v-rating
                  :value="data.horse.rating"
                  color="yellow darken-3"
                  background-color="grey darken-1"
                  readonly
                  dense
                ></v-rating>
                <span class="rating-text">{{ data.horse.rating }}/5</span>
              </div>
            </div>

            <!-- Horse Breed -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.breed')"
              :value="data.horse.breed || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Gender -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.gender')"
              :value="data.horse.gender || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Age -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.age')"
              :value="data.horse.age ? data.horse.age + ' ' + $t('PLACEHOLDERS.years') : $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Color -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.color')"
              :value="data.horse.color || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Height -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.height')"
              :value="data.horse.height ? data.horse.height + ' ' + $t('PLACEHOLDERS.cm') : $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Father -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.father')"
              :value="data.horse.father || $t('TABLES.noData')"
              disabled
            />

            <!-- Horse Mother -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.mother')"
              :value="data.horse.mother || $t('TABLES.noData')"
              disabled
            />

            <!-- Chip Number -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.chip_number')"
              :value="data.horse.chip_number || $t('TABLES.noData')"
              disabled
            />

            <!-- Health Status -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.health_status')"
              :value="data.horse.health_status || $t('TABLES.noData')"
              disabled
            />

            <!-- Training Level -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.training_level')"
              :value="data.horse.training_level || $t('TABLES.noData')"
              disabled
            />

            <!-- Pedigree Certificate -->
            <div class="col-6" v-if="data.horse.pedigree_certificate">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.pedigree_certificate") }}</h6>
              <a :href="data.horse.pedigree_certificate" target="_blank" class="btn btn-info">
                <i class="fal fa-download"></i> {{ $t("PLACEHOLDERS.download") }}
              </a>
            </div>

            <!-- Delivery Included -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.delivery_included") }}</label>
              <v-chip :color="data.horse.delivery_included ? 'green' : 'red'" text-color="white">
                {{ data.horse.delivery_included ? $t("PLACEHOLDERS.yes") : $t("PLACEHOLDERS.no") }}
              </v-chip>
            </div>

            <!-- Care Included -->
            <div class="col-6 my-5">
              <label class="form-label">{{ $t("PLACEHOLDERS.care_included") }}</label>
              <v-chip :color="data.horse.care_included ? 'green' : 'red'" text-color="white">
                {{ data.horse.care_included ? $t("PLACEHOLDERS.yes") : $t("PLACEHOLDERS.no") }}
              </v-chip>
            </div>

            <!-- Cancellation Policy -->
            <div class="col-12" v-if="data.horse.cancellation_policy">
              <h6 class="mb-3">{{ $t("PLACEHOLDERS.cancellation_policy") }}</h6>
              <a :href="data.horse.cancellation_policy" target="_blank" class="btn btn-info">
                <i class="fal fa-file-pdf"></i> {{ $t("HORSE_RENT.view_policy") }}
              </a>
            </div>
          </div>
        </div>
        <!-- ==== End:: Horse Data Section ==== -->

        <!-- ==== Start:: Booking Data Section ==== -->
        <div class="section-card mb-4">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.booking_data") }}</h5>
          <div class="row">
            <!-- Start Date -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.start_date')"
              :value="formatDate(data.rental_start_date)"
              disabled
            />

            <!-- Start Time -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.start_time')"
              :value="data.rental_start_time || $t('TABLES.noData')"
              disabled
            />

            <!-- Rental Duration -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.rental_duration')"
              :value="data.rental_duration ? data.rental_duration + ' ' + data.rental_duration_unit : $t('TABLES.noData')"
              disabled
            />

            <!-- End Date -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.end_date')"
              :value="formatDate(data.rental_end_date)"
              disabled
            />

            <!-- Total Price -->
            <base-input
              col="6"
              type="text"
              :placeholder="$t('PLACEHOLDERS.total_price')"
              :value="data.total ? data.total + ' ' + $t('PLACEHOLDERS.riyal') : $t('TABLES.noData')"
              disabled
            />
          </div>
        </div>
        <!-- ==== End:: Booking Data Section ==== -->

        <!-- Cancellation Reason (if cancelled or rejected) -->
        <div class="section-card mb-4" v-if="data.cancellation_reason && (data.status === 'cancelled' || data.status === 'rejected')">
          <h5 class="section-title">{{ $t("PLACEHOLDERS.cancellation_reason") }}</h5>
          <div class="row">
            <base-input
              col="12"
              type="textarea"
              :placeholder="$t('PLACEHOLDERS.cancellation_reason')"
              :value="data.cancellation_reason"
              disabled
            />
          </div>
        </div>
      </div>
    </div>
    <!-- End:: Single Step Form Content -->

    <!-- Start:: Image Modal -->
    <v-dialog v-model="dialogImage" max-width="800px">
      <v-card>
        <v-card-text class="text-center">
          <img :src="selectedImage" style="max-width: 100%; max-height: 70vh;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogImage = false">{{ $t("BUTTONS.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Image Modal -->
  </div>
</template>

<script>
export default {
  name: "ShowRoomRentOrder",

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      // End:: Loading Data

      // Start:: Image Modal
      dialogImage: false,
      selectedImage: null,
      // End:: Image Modal

      // Start:: Data
      data: {
        id: null,
        status: null,
        status_label: '',
        created_at: null,
        user: null,
        provider: null,
        horse: null,
        rental_start_date: null,
        rental_start_time: null,
        rental_duration: null,
        rental_duration_unit: null,
        rental_end_date: null,
        total: null,
        cancellation_reason: null,
      },
      // End:: Data
    };
  },

  methods: {
    // Start:: Helper Methods
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US');
    },

    getStatusColor(status) {
      const statusColors = {
        new: 'blue',
        accepted: 'green',
        rejected: 'red',
        current: 'orange',
        completed: 'teal',
        cancelled: 'grey'
      };
      return statusColors[status] || 'grey';
    },

    showImageModal(image) {
      this.selectedImage = image;
      this.dialogImage = true;
    },
    // End:: Helper Methods

    // Start:: Get Data To Show
    async getDataToShow() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `orders/${this.$route.params.id}`,
        });
        this.data = res.data.data.order;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || "Error loading order data");
        this.$message.error(error.response?.data?.message);
        this.$router.push({ path: "/room-rent/all" });
      }
    },
    // End:: Get Data To Show
  },

  created() {
    // Start:: Fire Methods
    this.getDataToShow();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.section-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.section-title {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin-bottom: 20px;
  font-weight: bold;
}

.preview-container {
  margin-bottom: 20px;
}

.user-avatar, .stable-avatar {
  max-width: 120px;
  max-height: 120px;
  border: 3px solid #ddd;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rating-text {
  font-weight: bold;
  color: #f39c12;
}

.map_wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #eee;
  margin-top: 10px;
}

.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.image-wrapper:hover {
  transform: scale(1.05);
}

.horse-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.horse-image:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .images-container {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .horse-image {
    height: 120px;
  }
  
  .user-avatar, .stable-avatar {
    max-width: 100px;
    max-height: 100px;
  }
}
</style>